import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import {
  AppstoreOutlined,
  BankOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CheckSquareOutlined,
  CloseOutlined,
  CopyOutlined,
  DollarOutlined,
  DownOutlined,
  EnvironmentOutlined,
  ExportOutlined,
  ReloadOutlined,
  RocketOutlined,
  FileProtectOutlined,
  FileTextOutlined,
  GiftOutlined,
  SearchOutlined,
  StarOutlined,
  LoadingOutlined,
  ThunderboltOutlined,
  UsergroupAddOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Empty, Pagination, Segmented, Select, Spin } from "antd";
import { COUNTRY_OPTIONS } from "../config/countries";
import { JOB_SKILLS } from "../config/jobSkills";
import apiClient from "../actions/api";
import { updateBalance } from "../actions/auth";
import JobResumeWindow from "../components/JobResumeWindow";
import "../style/job-board.scss";

const DEFAULT_FILTERS = {
  date: ["24 hours"],
  location: [],
  workplace: [],
  employment: [],
  experience: [],
  salary: [],
  skills: [],
  easy: [],
  h1b: [],
  applicationDays: ["20"],
};
const URL_FILTER_KEYS = Object.keys(DEFAULT_FILTERS);
const getInitialUrlState = () => {
  const params = new URLSearchParams(window.location.search);
  const parsedFilters = Object.fromEntries(
    URL_FILTER_KEYS.map((key) => {
      const values = params.getAll(key).filter(Boolean);
      return [key, values.length ? values : [...DEFAULT_FILTERS[key]]];
    }),
  );
  const parsedPage = Number.parseInt(params.get("page"), 10);
  parsedFilters.applicationDays = [
    String(
      Math.min(
        3650,
        Math.max(
          1,
          Number.parseInt(parsedFilters.applicationDays[0], 10) || 20,
        ),
      ),
    ),
  ];

  return {
    query: params.get("q") || "",
    filters: parsedFilters,
    sort: params.get("sort") || "recommended",
    page: Number.isInteger(parsedPage) && parsedPage > 0 ? parsedPage : 1,
  };
};
const filters = [
  { key: "date", label: "24 hours", icon: <ClockCircleOutlined /> },
  { key: "location", label: "Location", icon: <EnvironmentOutlined /> },
  { key: "workplace", label: "Workplace", icon: <EnvironmentOutlined /> },
  {
    key: "employment",
    label: "Employment Type",
    icon: <UsergroupAddOutlined />,
  },
  { key: "experience", label: "Experience Level", icon: <ExportOutlined /> },
  { key: "salary", label: "Salary", icon: <DollarOutlined /> },
  { key: "skills", label: "Skills", icon: <StarOutlined /> },
  {
    key: "easy",
    label: "Easy Apply",
    icon: <ThunderboltOutlined />,
    simple: true,
  },
  {
    key: "h1b",
    label: "H1B Sponsorship",
    icon: <AppstoreOutlined />,
    simple: true,
  },
  {
    key: "applicationDays",
    label: "Application History",
    icon: <ClockCircleOutlined />,
  },
];
const choices = {
  date: ["24 hours", "3 days", "7 days", "1 month", "3 months"],
  workplace: ["Onsite", "Hybrid", "Remote", "Not Specified"],
  employment: [
    "Full-time",
    "Part-time",
    "Contractor",
    "Temporary",
    "Intern",
    "Volunteer",
    "Per-diem",
    "Other",
  ],
  experience: [
    "Entry Level",
    "Mid Level",
    "Senior",
    "Executive",
    "Internship",
    "Associate",
    "Director",
    "Not Specified",
  ],
};
const slug = (value) => value.toLowerCase().replace(/\s+/g, "-");
const salaryValue = (values, prefix) =>
  values.find((x) => x.startsWith(prefix))?.slice(prefix.length) || "";
const countryName = (code) =>
  COUNTRY_OPTIONS.find((item) => item.value === code)?.label || code;
const platformDomain = (value = "") =>
  String(value)
    .trim()
    .replace(/^https?:\/\//i, "")
    .split(/[/?#]/)[0]
    .replace(/^www\./i, "")
    .toLowerCase();
const relativeDate = (value) => {
  if (!value) return "Recently";
  const hours = Math.max(
    0,
    Math.floor((Date.now() - new Date(value).getTime()) / 3600000),
  );
  if (hours < 1) return "Just now";
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
};
const normalizeCompanyName = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
const findApplicationForCompany = (applications, companyName) => {
  const jobCompany = normalizeCompanyName(companyName);
  if (!jobCompany) return null;
  return (
    applications.find((application) => {
      const appliedCompany = normalizeCompanyName(application.company_name);
      return appliedCompany && jobCompany.includes(appliedCompany);
    }) || null
  );
};
const submittedDate = (value) => {
  const submitted = new Date(value);
  if (Number.isNaN(submitted.getTime())) return "previously";
  const today = new Date();
  const submittedDay = new Date(
    submitted.getFullYear(),
    submitted.getMonth(),
    submitted.getDate(),
  );
  const todayDay = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const days = Math.max(
    0,
    Math.round((todayDay.getTime() - submittedDay.getTime()) / 86400000),
  );
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  return `${days} days ago`;
};
const logoColor = (text = "") => {
  const palette = ["#087b85", "#256bfe", "#7654d6", "#b56917", "#147c5a"];
  return palette[
    [...text].reduce((sum, char) => sum + char.charCodeAt(0), 0) %
      palette.length
  ];
};
const displayValue = (value) => {
  const values = Array.isArray(value) ? value : value ? [value] : [];
  return values.length
    ? values
        .map((item) =>
          String(item)
            .replace(/-/g, " ")
            .replace(/\b\w/g, (letter) => letter.toUpperCase()),
        )
        .join(", ")
    : "Not specified";
};
const formatSalary = (salary) => {
  if (!salary || (salary.min == null && salary.max == null)) return "";
  const currency = salary.currency || "USD";
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  });
  const range =
    salary.min != null && salary.max != null
      ? `${formatter.format(salary.min)} – ${formatter.format(salary.max)}`
      : salary.min != null
        ? `From ${formatter.format(salary.min)}`
        : `Up to ${formatter.format(salary.max)}`;
  return `${range} a ${salary.unit || "year"}`;
};
const mapListJob = (job) => ({
  ...job,
  title: job.position || "Untitled role",
  companyName: job.companyName || "Company not specified",
  age: relativeDate(job.postedDate || job.scannedDate),
  locationLabel: Array.isArray(job.location)
    ? job.location
        .map((item) => item.fullName || item.originalText || item.country)
        .filter(Boolean)
        .join(" · ")
    : job.location?.fullName || job.location || "",
  visaLabel:
    job.visaStatus === "likely-sponsor"
      ? "Likely Sponsor Visa"
      : job.visaStatus === "not-sponsor"
        ? "Does not sponsor"
        : "Sponsorship unknown",
  tags: Array.isArray(job.skills) ? job.skills : [],
  platforms: (Array.isArray(job.platforms) ? job.platforms : [])
    .map((item) => platformDomain(item?.platform || item))
    .filter(Boolean),
  logo: (job.companyName || "C").trim().charAt(0).toUpperCase(),
  color: logoColor(job.companyName),
});

function FilterPanel({
  type,
  onConfirm,
  selected = [],
  onChange,
  style,
  jobCount,
}) {
  const toggle = (value) =>
    onChange(
      selected.includes(value)
        ? selected.filter((item) => item !== value)
        : [...selected, value],
    );
  const list = choices[type];
  return (
    <div
      className={`job-filter-panel panel-${type}`}
      style={style}
      onClick={(event) => event.stopPropagation()}
    >
      <strong>{filters.find((filter) => filter.key === type)?.label}</strong>
      {type === "location" && (
        <Select
          popupClassName="job-filter-select-dropdown"
          mode="multiple"
          showSearch
          allowClear
          value={selected}
          onChange={onChange}
          optionFilterProp="label"
          placeholder="Select countries"
          options={COUNTRY_OPTIONS}
        />
      )}
      {type === "skills" && (
        <Select
          popupClassName="job-filter-select-dropdown"
          mode="tags"
          allowClear
          showSearch
          optionFilterProp="label"
          value={selected}
          onChange={onChange}
          placeholder="Select or enter skills"
          tokenSeparators={[","]}
          options={JOB_SKILLS.map((value) => ({ value, label: value }))}
        />
      )}
      {type === "salary" && (
        <>
          <small>Salary range</small>
          <div className="salary-row">
            <input
              type="number"
              min="0"
              inputMode="numeric"
              value={salaryValue(selected, "Min: ")}
              onChange={(event) =>
                onChange([
                  ...selected.filter((x) => !x.startsWith("Min: ")),
                  ...(event.target.value ? [`Min: ${event.target.value}`] : []),
                ])
              }
              placeholder="$  Min"
            />
            <input
              type="number"
              min="0"
              inputMode="numeric"
              value={salaryValue(selected, "Max: ")}
              onChange={(event) =>
                onChange([
                  ...selected.filter((x) => !x.startsWith("Max: ")),
                  ...(event.target.value ? [`Max: ${event.target.value}`] : []),
                ])
              }
              placeholder="$  Max"
            />
          </div>
          <small>Currency & period</small>
          <div className="salary-row">
            <Select
              popupClassName="job-filter-select-dropdown"
              value={salaryValue(selected, "Currency: ") || "USD ($)"}
              onChange={(value) =>
                onChange([
                  ...selected.filter((x) => !x.startsWith("Currency: ")),
                  ...(value === "USD ($)" ? [] : [`Currency: ${value}`]),
                ])
              }
              options={["USD ($)", "EUR (€)", "GBP (£)", "CAD (C$)"].map(
                (value) => ({ value, label: value }),
              )}
            />
            <Select
              popupClassName="job-filter-select-dropdown"
              value={salaryValue(selected, "Period: ") || "Yearly"}
              onChange={(value) =>
                onChange([
                  ...selected.filter((x) => !x.startsWith("Period: ")),
                  ...(value === "Yearly" ? [] : [`Period: ${value}`]),
                ])
              }
              options={["Yearly", "Monthly", "Hourly"].map((value) => ({
                value,
                label: value,
              }))}
            />
          </div>
        </>
      )}
      {type === "applicationDays" && (
        <div className="application-days-filter">
          <p>
            Choose how many past days of your application history should be
            checked. Jobs from matching companies will be highlighted. This does
            not change your job search results or other filters.
          </p>
          <label htmlFor="application-history-days">Days to check</label>
          <input
            id="application-history-days"
            type="number"
            min="1"
            max="3650"
            inputMode="numeric"
            value={selected[0] || "20"}
            onChange={(event) => {
              const value = event.target.value;
              onChange(value ? [value] : []);
            }}
            placeholder="20"
          />
          <small>Default: applications submitted in the last 20 days.</small>
        </div>
      )}
      {list && (
        <div className="choice-list">
          {list.map((value) => (
            <label
              className={selected.includes(value) ? "selected" : ""}
              key={value}
            >
              <input
                type={type === "date" ? "radio" : "checkbox"}
                checked={selected.includes(value)}
                onChange={() =>
                  type === "date" ? onChange([value]) : toggle(value)
                }
              />
              <span>{value}</span>
            </label>
          ))}
        </div>
      )}
      <button className="confirm-filter" onClick={onConfirm}>
        Confirm · {jobCount} jobs
      </button>
    </div>
  );
}

export default function JobBoard({ title }) {
  document.title = title;
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth.user);
  const [initialUrlState] = useState(getInitialUrlState);
  const [boardConfig, setBoardConfig] = useState(null);
  const profileId = useMemo(
    () =>
      new URLSearchParams(window.location.search).get("profileId") ||
      localStorage.getItem("jobBoardProfileId") ||
      authUser?.profileId ||
      boardConfig?.fallbackProfileId ||
      "",
    [authUser?.profileId, boardConfig?.fallbackProfileId],
  );
  const [activeFilter, setActiveFilter] = useState(null);
  const [query, setQuery] = useState(initialUrlState.query);
  const [debouncedQuery, setDebouncedQuery] = useState(
    initialUrlState.query.trim(),
  );
  const [selectedId, setSelectedId] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [jobDetail, setJobDetail] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");
  const [sort, setSort] = useState(initialUrlState.sort);
  const [page, setPage] = useState(initialUrlState.page);
  const pageSize = 20;
  const [filterValues, setFilterValues] = useState(initialUrlState.filters);
  const [draftFilter, setDraftFilter] = useState([]);
  const [panelPosition, setPanelPosition] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);
  const [copyState, setCopyState] = useState("idle");
  const [copyError, setCopyError] = useState("");
  const [applyLoading, setApplyLoading] = useState(false);
  const [urlPurchased, setUrlPurchased] = useState(null);
  const [purchaseStatusLoading, setPurchaseStatusLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [resumeWindows, setResumeWindows] = useState([]);
  const [activeResumeWindow, setActiveResumeWindow] = useState(null);
  const resumeProfiles = useMemo(
    () =>
      authUser?.resume_documents?.length
        ? authUser.resume_documents
        : authUser?.resume_document_url
          ? [{ _id: "legacy", name: "Existing Resume" }]
          : [],
    [authUser?.resume_document_url, authUser?.resume_documents],
  );
  const [buildChooserOpen, setBuildChooserOpen] = useState(false);
  const [buildSourceType, setBuildSourceType] = useState(
    resumeProfiles.length ? "resume_document" : "template",
  );
  const [buildResumeProfileId, setBuildResumeProfileId] = useState("");

  useEffect(() => {
    if (!buildResumeProfileId && resumeProfiles.length) {
      setBuildResumeProfileId(resumeProfiles[0]._id);
    }
  }, [buildResumeProfileId, resumeProfiles]);

  const applicationDays = Math.min(
    3650,
    Math.max(1, Number.parseInt(filterValues.applicationDays[0], 10) || 20),
  );
  const loadApplications = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/job-board/applications", {
        params: { days: applicationDays },
      });
      setApplications(
        Array.isArray(data?.applications) ? data.applications : [],
      );
    } catch (_error) {
      // Keep this background refresh silent so job browsing is uninterrupted.
    }
  }, [applicationDays]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 350);
    return () => clearTimeout(timer);
  }, [query]);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    ["q", "sort", "page", ...URL_FILTER_KEYS].forEach((key) =>
      params.delete(key),
    );
    if (query.trim()) params.set("q", query.trim());
    if (sort !== "recommended") params.set("sort", sort);
    if (page > 1) params.set("page", String(page));
    URL_FILTER_KEYS.forEach((key) => {
      const values = filterValues[key] || [];
      const isDefaultDate =
        key === "date" && values.length === 1 && values[0] === "24 hours";
      if (!isDefaultDate) {
        values.forEach((value) => params.append(key, value));
      }
    });

    const search = params.toString();
    const nextUrl = `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`;
    window.history.replaceState(window.history.state, "", nextUrl);
  }, [filterValues, page, query, sort]);
  useEffect(() => {
    apiClient
      .get("/job-board/config")
      .then(({ data }) => setBoardConfig(data))
      .catch(() => setError("Job Board is not configured."));
  }, []);
  useEffect(() => {
    loadApplications();
    const timer = window.setInterval(loadApplications, 60000);
    return () => window.clearInterval(timer);
  }, [loadApplications]);

  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    params.set("searchTerm", debouncedQuery);
    params.set("title", "");
    params.set("company", "");
    filterValues.skills.forEach((value) => params.append("skills[]", value));
    params.set("datePosted", filterValues.date[0] || "24 hours");
    if (filterValues.location.length) {
      filterValues.location.forEach((value) =>
        params.append("location", countryName(value)),
      );
      params.set("locationType", "country");
    } else {
      params.set("location", "");
      params.set("locationType", "");
    }
    filterValues.workplace.forEach((value) =>
      params.append("locationTypes[]", slug(value)),
    );
    filterValues.employment.forEach((value) =>
      params.append("employmentTypes[]", slug(value)),
    );
    filterValues.experience.forEach((value) =>
      params.append("experienceLevels[]", slug(value)),
    );
    params.set("h1bSponsorship", String(filterValues.h1b.length > 0));
    params.set("industries", "");
    params.set("salary[min]", salaryValue(filterValues.salary, "Min: "));
    params.set("salary[max]", salaryValue(filterValues.salary, "Max: "));
    params.set(
      "salary[unit]",
      (salaryValue(filterValues.salary, "Period: ") || "Yearly")
        .toLowerCase()
        .replace("ly", ""),
    );
    params.set(
      "salary[currency]",
      (salaryValue(filterValues.salary, "Currency: ") || "USD ($)").split(
        " ",
      )[0],
    );
    params.set("salary[isEquityOnly]", "false");
    params.set("easyApply", String(filterValues.easy.length > 0));
    params.set("sortedBy", sort);
    params.set("jobView", "all");
    params.set("page", String(page));
    params.set("pageSize", String(pageSize));
    params.set("profileId", profileId);
    return params;
  }, [debouncedQuery, filterValues, page, pageSize, profileId, sort]);

  useEffect(() => {
    let current = true;
    const loadJobs = async () => {
      if (!boardConfig?.boardApi || !profileId) return;
      setLoading(true);
      setError("");
      try {
        const { data } = await axios.get(
          `${boardConfig.boardApi}/jobs/public?${buildParams().toString()}`,
        );
        if (!current) return;
        const mapped = (Array.isArray(data?.jobs) ? data.jobs : []).map(
          mapListJob,
        );
        setJobs(mapped);
        setTotalCount(Number(data?.totalCount) || mapped.length);
        setSelectedId((old) =>
          mapped.some((job) => job.id === old) ? old : mapped[0]?.id || null,
        );
      } catch (requestError) {
        if (current) {
          setError(
            requestError?.response?.data?.message ||
              "Unable to load jobs. Please try again.",
          );
        }
      } finally {
        if (current) setLoading(false);
      }
    };
    loadJobs();
    return () => {
      current = false;
    };
  }, [boardConfig?.boardApi, buildParams, profileId, refreshKey]);

  useEffect(() => {
    if (!selectedId || !boardConfig?.boardApi) {
      setJobDetail(null);
      return;
    }
    let current = true;
    setDetailLoading(true);
    axios
      .get(`${boardConfig.boardApi}/job/${selectedId}/info`, {
        params: { profileId },
      })
      .then(({ data }) => {
        if (current) setJobDetail(data);
      })
      .catch(() => {
        if (current) setJobDetail(null);
      })
      .finally(() => {
        if (current) setDetailLoading(false);
      });
    return () => {
      current = false;
    };
  }, [boardConfig?.boardApi, profileId, selectedId]);

  const selectedListJob = jobs.find((job) => job.id === selectedId);
  const selected = selectedListJob || null;
  useEffect(() => {
    if (!selected?.id || !profileId) {
      setUrlPurchased(null);
      setPurchaseStatusLoading(true);
      return;
    }
    let current = true;
    setUrlPurchased(null);
    setPurchaseStatusLoading(true);
    setCopyError("");
    apiClient
      .get("/job-board/purchase-status", {
        params: {
          jobId: selected.id,
          profileId,
          companyName: selected.companyName,
          position: selected.title,
        },
      })
      .then(({ data }) => {
        if (current) {
          setUrlPurchased(Boolean(data?.purchased));
          setPurchaseStatusLoading(false);
        }
      })
      .catch((requestError) => {
        if (current) {
          // Fall back to the purchasable state. The job URL endpoint performs
          // the authoritative duplicate-purchase check before charging.
          setUrlPurchased(false);
          setPurchaseStatusLoading(false);
          setCopyError(
            requestError?.response?.data?.message ||
              "Unable to verify whether this job URL was purchased.",
          );
        }
      });
    return () => {
      current = false;
    };
  }, [profileId, selected?.companyName, selected?.id, selected?.title]);
  const selectedApplication = selected
    ? findApplicationForCompany(applications, selected.companyName)
    : null;
  const detailLocation =
    jobDetail?.location?.fullName ||
    jobDetail?.location?.originalText ||
    selected?.locationLabel ||
    "";
  const detailEmployment =
    jobDetail?.employmentType ?? selected?.employmentType;
  const detailSalary = jobDetail?.salary ?? selected?.salary;
  const requiredSkills = Array.isArray(jobDetail?.skills?.requiredSkills)
    ? jobDetail.skills.requiredSkills
    : [];
  const bonusSkills = Array.isArray(jobDetail?.skills?.bonusSkills)
    ? jobDetail.skills.bonusSkills
    : [];
  const responsibilities = Array.isArray(jobDetail?.shortInfo?.responsibilities)
    ? jobDetail.shortInfo.responsibilities
    : [];
  const requirements = Array.isArray(jobDetail?.shortInfo?.requirements)
    ? jobDetail.shortInfo.requirements
    : [];
  const benefits = Array.isArray(jobDetail?.shortInfo?.benefits)
    ? jobDetail.shortInfo.benefits
    : [];
  const visaStatus = jobDetail?.visaStatus ?? selected?.visaStatus;
  const detailVisa =
    visaStatus === "likely-sponsor"
      ? "Likely Sponsor Visa"
      : visaStatus === "not-sponsor"
        ? "Does not sponsor"
        : visaStatus
          ? displayValue(visaStatus)
          : "";
  const resumeDescription = (() => {
    const sections = [
      ["Location", detailLocation],
      ["Employment Type", displayValue(detailEmployment)],
      ["Salary", formatSalary(detailSalary)],
      ["Visa Sponsorship", detailVisa],
      ["Required Skills", requiredSkills],
      ["Bonus Skills", bonusSkills],
      ["About Company", jobDetail?.shortInfo?.aboutCompany],
      ["About Role", jobDetail?.shortInfo?.aboutRole],
      ["Responsibilities", responsibilities],
      ["Requirements", requirements],
      ["Benefits", benefits],
    ];
    return sections
      .filter(([, value]) =>
        Array.isArray(value) ? value.length > 0 : Boolean(value),
      )
      .map(
        ([label, value]) =>
          `${label}\n${Array.isArray(value) ? value.join("\n") : value}`,
      )
      .join("\n\n");
  })();
  const canBuildResume =
    !detailLoading &&
    Boolean(jobDetail) &&
    (responsibilities.length > 0 ||
      requirements.length > 0 ||
      benefits.length > 0);
  const selectedCompanyName = jobDetail?.company || selected?.companyName || "";
  const openedCompanyWindow = resumeWindows.find(
    (item) =>
      normalizeCompanyName(item.company) ===
      normalizeCompanyName(selectedCompanyName),
  );
  const canOpenResumeWindow =
    canBuildResume && !openedCompanyWindow && !selectedApplication;
  const openResumeWindow = () => {
    if (!selected || !canBuildResume || selectedApplication) return;
    if (openedCompanyWindow) {
      setActiveResumeWindow(openedCompanyWindow.id);
      return;
    }
    setResumeWindows((old) => [
      ...old,
      {
        id: selected.id,
        company: selectedCompanyName,
        position: jobDetail?.position || selected.title,
        description: resumeDescription,
        sourceType: buildSourceType,
        resumeProfileId:
          buildSourceType === "resume_document"
            ? buildResumeProfileId
            : undefined,
      },
    ]);
    setActiveResumeWindow(selected.id);
    setBuildChooserOpen(false);
  };
  const closeResumeWindow = (id) => {
    setResumeWindows((old) => old.filter((item) => item.id !== id));
    setActiveResumeWindow((old) => (old === id ? null : old));
  };
  const copyJobLink = async () => {
    setCopyState("loading");
    setCopyError("");
    try {
      const { data } = await apiClient.get("/job-board/job-url", {
        params: {
          jobId: selected.id,
          profileId,
          companyName: jobDetail?.company || selected.companyName,
          position: jobDetail?.position || selected.title,
        },
      });
      if (!data?.url) throw new Error("Missing job URL");
      setUrlPurchased(true);
      setPurchaseStatusLoading(false);
      if (data.balance != null) dispatch(updateBalance(data.balance));
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(data.url);
      } else {
        const input = document.createElement("textarea");
        input.value = data.url;
        input.style.position = "fixed";
        input.style.opacity = "0";
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        input.remove();
      }
      setCopyState("copied");
      setTimeout(() => setCopyState("idle"), 1800);
    } catch (requestError) {
      setCopyState("idle");
      setCopyError(
        requestError?.response?.data?.message || "Unable to copy this job URL.",
      );
    }
  };
  const openApplication = async () => {
    const popup = window.open("about:blank", "_blank");
    setApplyLoading(true);
    setCopyError("");
    try {
      const { data } = await apiClient.get("/job-board/job-url", {
        params: {
          jobId: selected.id,
          profileId,
          companyName: jobDetail?.company || selected.companyName,
          position: jobDetail?.position || selected.title,
        },
      });
      if (!data?.url) throw new Error("Missing job URL");
      setUrlPurchased(true);
      setPurchaseStatusLoading(false);
      if (data.balance != null) dispatch(updateBalance(data.balance));
      if (popup) {
        popup.opener = null;
        popup.location.replace(data.url);
      } else {
        window.open(data.url, "_blank", "noopener,noreferrer");
      }
    } catch (requestError) {
      popup?.close();
      setCopyError(
        requestError?.response?.data?.message || "Unable to open this job URL.",
      );
    } finally {
      setApplyLoading(false);
    }
  };
  const toggleFilter = (filter, element) => {
    if (filter.simple) {
      setFilterValues((old) => ({
        ...old,
        [filter.key]: old[filter.key]?.length ? [] : [filter.label],
      }));
      setPage(1);
      return;
    }
    if (activeFilter === filter.key) {
      setActiveFilter(null);
      return;
    }
    const rect = element.getBoundingClientRect();
    const width = filter.key === "salary" ? 445 : 310;
    setPanelPosition({
      top: rect.bottom + 8,
      left: Math.max(12, Math.min(rect.left, window.innerWidth - width - 12)),
      maxHeight: Math.max(220, window.innerHeight - rect.bottom - 20),
    });
    setDraftFilter([...(filterValues[filter.key] || [])]);
    setActiveFilter(filter.key);
  };
  const chipLabel = (filter) => {
    const values = filterValues[filter.key] || [];
    if (filter.simple) return filter.label;
    if (filter.key === "date" && values[0]) return values[0];
    if (filter.key === "applicationDays") {
      return `Applications: ${applicationDays} days`;
    }
    if (
      values.length === 1 &&
      ["location", "workplace", "employment", "experience"].includes(filter.key)
    )
      return filter.key === "location" ? countryName(values[0]) : values[0];
    return values.length ? (
      <>
        {filter.label}
        <b className="filter-count">{values.length}</b>
      </>
    ) : (
      filter.label
    );
  };
  const hasClearableFilters = Object.entries(filterValues).some(
    ([key, values]) =>
      key === "date"
        ? values.length !== 1 || values[0] !== "24 hours"
        : key === "applicationDays"
          ? values.length !== 1 || values[0] !== "20"
          : values.length > 0,
  );

  return (
    <div className="job-board" onClick={() => setActiveFilter(null)}>
      <div className="job-board-heading">
        <div>
          <span className="eyebrow">OPPORTUNITY EXPLORER</span>
          <h1>Find your next role</h1>
          <p>
            Discover roles matched to your experience, skills, and preferences.
          </p>
        </div>
        <div className="board-stat">
          <strong>{totalCount}</strong>
          <span>Available roles</span>
        </div>
      </div>
      <div className="job-search">
        <SearchOutlined />
        <input
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setPage(1);
          }}
          placeholder={
            'Search by job title — e.g. "Software Engineer", "Product Manager", "Data Analyst"...'
          }
        />
        {query && (
          <CloseOutlined
            onClick={() => {
              setQuery("");
              setPage(1);
            }}
          />
        )}
      </div>
      <div className="job-filters">
        {filters.map((filter) => {
          const enabled = (filterValues[filter.key] || []).length > 0;
          return (
            <div className="filter-wrap" key={filter.key}>
              <button
                className={`filter-chip ${activeFilter === filter.key || enabled ? "active" : ""}`}
                onClick={(event) => {
                  event.stopPropagation();
                  toggleFilter(filter, event.currentTarget);
                }}
              >
                {enabled ? <CheckOutlined /> : filter.icon}
                <span>{chipLabel(filter)}</span>
                {!filter.simple && (
                  <DownOutlined
                    className={`filter-chevron ${activeFilter === filter.key ? "open" : ""}`}
                  />
                )}
              </button>
              {activeFilter === filter.key &&
                createPortal(
                  <FilterPanel
                    type={filter.key}
                    selected={draftFilter}
                    onChange={setDraftFilter}
                    style={panelPosition}
                    jobCount={totalCount}
                    onConfirm={() => {
                      const confirmedFilter =
                        filter.key === "applicationDays"
                          ? [
                              String(
                                Math.min(
                                  3650,
                                  Math.max(
                                    1,
                                    Number.parseInt(draftFilter[0], 10) || 20,
                                  ),
                                ),
                              ),
                            ]
                          : draftFilter;
                      setFilterValues((old) => ({
                        ...old,
                        [filter.key]: confirmedFilter,
                      }));
                      setPage(1);
                      setActiveFilter(null);
                    }}
                  />,
                  document.body,
                )}
            </div>
          );
        })}
        <div
          className={`filter-actions ${hasClearableFilters ? "has-clear" : ""}`}
        >
          {hasClearableFilters && (
            <button
              className="clear-filters"
              onClick={() => {
                setActiveFilter(null);
                setDraftFilter([]);
                setFilterValues(DEFAULT_FILTERS);
                setPage(1);
              }}
            >
              <CloseOutlined />
              <span>Clear all</span>
            </button>
          )}
          <button
            className="refresh"
            title="Refresh results using selected filters"
            onClick={() => {
              setActiveFilter(null);
              setRefreshKey((value) => value + 1);
              loadApplications();
            }}
          >
            <ReloadOutlined />
          </button>
        </div>
      </div>
      <div className="board-body">
        <aside className="jobs-column">
          <div className="list-toolbar">
            <div>
              <button className="active">
                All Jobs <b>{totalCount}</b>
              </button>
            </div>
            <div>
              <button
                className={sort === "datePosted" ? "active" : ""}
                onClick={() => {
                  setSort("datePosted");
                  setPage(1);
                }}
              >
                Newest
              </button>
              <button
                className={sort === "recommended" ? "active" : ""}
                onClick={() => {
                  setSort("recommended");
                  setPage(1);
                }}
              >
                Recommended
              </button>
            </div>
          </div>
          <div className="job-list">
            {error && !jobs.length ? (
              <div className="empty-jobs">{error}</div>
            ) : jobs.length ? (
              jobs.map((job) => {
                const previousApplication = findApplicationForCompany(
                  applications,
                  job.companyName,
                );
                return (
                  <article
                    key={job.id}
                    className={`job-card ${selectedId === job.id ? "active" : ""}`}
                    onClick={() => {
                      if (job.id !== selectedId) setJobDetail(null);
                      setSelectedId(job.id);
                    }}
                  >
                    <div className="card-top">
                      <div
                        className="company-logo"
                        style={{ background: job.color }}
                      >
                        {job.logo}
                      </div>
                      <div className="card-title">
                        <h3>{job.title}</h3>
                        <p
                          className={
                            previousApplication ? "company-submitted" : ""
                          }
                        >
                          {job.companyName}
                        </p>
                      </div>
                      {job.platforms.length > 0 && (
                        <div
                          className="job-platforms"
                          aria-label={`Available on ${job.platforms.join(", ")}`}
                        >
                          {job.platforms.slice(0, 3).map((platform, index) => (
                            <span
                              className="job-platform-icon"
                              key={`${platform}-${index}`}
                              data-platform={platform}
                              aria-label={platform}
                            >
                              <span aria-hidden="true">
                                {platform.charAt(0).toUpperCase()}
                              </span>
                              <img
                                src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(platform)}&sz=64`}
                                alt={`${platform} icon`}
                                loading="lazy"
                                onError={(event) => {
                                  event.currentTarget.style.display = "none";
                                }}
                              />
                            </span>
                          ))}
                          {job.platforms.length > 3 && (
                            <span
                              className="job-platform-more"
                              title={job.platforms.slice(3).join(", ")}
                            >
                              +{job.platforms.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="job-age">
                      <ClockCircleOutlined /> {job.age}
                    </div>
                    <div className="tag-row">
                      {job.tags.slice(0, 4).map((tag) => (
                        <span key={tag}>{tag}</span>
                      ))}
                      {job.tags.length > 4 && (
                        <span>+{job.tags.length - 4} more</span>
                      )}
                    </div>
                  </article>
                );
              })
            ) : (
              <Empty
                className="empty-jobs"
                description="No jobs match these filters"
              />
            )}
            {loading && (
              <div className="jobs-loading-overlay">
                <Spin />
              </div>
            )}
          </div>
          {totalCount > pageSize && (
            <div className="jobs-pagination">
              <Pagination
                current={page}
                pageSize={pageSize}
                total={totalCount}
                showSizeChanger={false}
                onChange={(nextPage) => {
                  setPage(nextPage);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            </div>
          )}
        </aside>
        <main className="job-detail">
          {!selected ? (
            <Empty
              className="detail-empty"
              description="Select a job to view details"
            />
          ) : (
            <>
              <header>
                <div
                  className="company-logo large"
                  style={{ background: selected.color }}
                >
                  {selected.logo}
                </div>
                <div>
                  <h2>{jobDetail?.position || selected.title}</h2>
                  <p>
                    <b
                      className={selectedApplication ? "company-submitted" : ""}
                    >
                      {jobDetail?.company || selected.companyName}
                    </b>
                    {selectedApplication && (
                      <span className="submitted-warning">
                        You already applied{" "}
                        {submittedDate(selectedApplication.date)}
                      </span>
                    )}{" "}
                    · Found {selected.age}
                    {(jobDetail?.industry || selected.industry) && (
                      <> · {jobDetail?.industry || selected.industry}</>
                    )}
                  </p>
                </div>
              </header>
              <div className="apply-bar">
                <button
                  className={`apply ${urlPurchased === false ? "unpurchased" : ""}`}
                  onClick={openApplication}
                  disabled={
                    detailLoading ||
                    !jobDetail ||
                    applyLoading ||
                    purchaseStatusLoading
                  }
                >
                  {applyLoading ? <LoadingOutlined spin /> : <ExportOutlined />}{" "}
                  {applyLoading ? "Opening…" : "Apply"}
                </button>
                <button
                  className={`pin-detail ${copyState === "copied" ? "copied" : ""}`}
                  title={
                    copyState === "copied" ? "Job URL copied" : "Copy job URL"
                  }
                  onClick={copyJobLink}
                  disabled={
                    detailLoading ||
                    !jobDetail ||
                    copyState === "loading" ||
                    purchaseStatusLoading
                  }
                >
                  {copyState === "loading" ? (
                    <LoadingOutlined spin />
                  ) : copyState === "copied" ? (
                    <CheckOutlined />
                  ) : (
                    <CopyOutlined />
                  )}
                </button>
                <div className="build-resume-control">
                <button
                  className="build-resume-detail"
                  title={
                    selectedApplication
                      ? `You already applied to ${selectedCompanyName}`
                      : openedCompanyWindow
                        ? `A resume window for ${selectedCompanyName} is already open`
                        : canBuildResume
                          ? "Build a resume for this job"
                          : "Job responsibilities, requirements, or benefits are required"
                  }
                  onClick={() => setBuildChooserOpen((open) => !open)}
                  disabled={!canOpenResumeWindow}
                >
                  <RocketOutlined /> Build Resume
                </button>
                {buildChooserOpen && canOpenResumeWindow && (
                  <div
                    className="build-resume-chooser"
                    role="dialog"
                    aria-label="Choose resume source"
                  >
                    <div className="build-chooser-header">
                      <span className="build-chooser-icon">
                        <RocketOutlined />
                      </span>
                      <div>
                        <strong>Build your resume</strong>
                        <small>Choose the source you want to tailor</small>
                      </div>
                      <button
                        type="button"
                        className="build-chooser-close"
                        onClick={() => setBuildChooserOpen(false)}
                        aria-label="Close resume source chooser"
                      >
                        <CloseOutlined />
                      </button>
                    </div>
                    <Segmented
                      block
                      className="build-source-segmented"
                      value={buildSourceType}
                      onChange={setBuildSourceType}
                      options={[
                        {
                          label: <><FileTextOutlined /> Templates</>,
                          value: "template",
                        },
                        {
                          label: <><UserOutlined /> Profiles</>,
                          value: "resume_document",
                        },
                      ]}
                    />
                    <p className="build-source-help">
                      {buildSourceType === "template"
                        ? "Use your formatted resume template."
                        : "Tailor one of your saved resume profiles."}
                    </p>
                    {buildSourceType === "resume_document" && (
                      <div className="build-profile-field">
                        <label>Resume profile</label>
                        <Select
                          size="large"
                          value={buildResumeProfileId || undefined}
                          placeholder="Choose a resume profile"
                          onChange={setBuildResumeProfileId}
                          options={resumeProfiles.map((profile) => ({
                            label: profile.name,
                            value: profile._id,
                          }))}
                          notFoundContent="No resume profiles available"
                        />
                      </div>
                    )}
                    <button
                      type="button"
                      className="build-resume-generate"
                      onClick={openResumeWindow}
                      disabled={
                        (buildSourceType === "template" && !authUser?.template_url) ||
                        (buildSourceType === "resume_document" && !buildResumeProfileId)
                      }
                    >
                      <RocketOutlined /> Generate
                    </button>
                  </div>
                )}
                </div>
                {copyError && (
                  <small className="apply-error">{copyError}</small>
                )}
              </div>
              <div className="detail-scroll">
                {detailLoading && (
                  <div className="detail-loading-overlay">
                    <Spin />
                  </div>
                )}
                <>
                  {(detailLocation ||
                    (Array.isArray(detailEmployment)
                      ? detailEmployment.length
                      : detailEmployment) ||
                    formatSalary(detailSalary) ||
                    detailVisa) && (
                    <div className="facts">
                      {detailLocation && (
                        <div>
                          <small>
                            <EnvironmentOutlined /> Location
                          </small>
                          <b>{detailLocation}</b>
                        </div>
                      )}
                      {(Array.isArray(detailEmployment)
                        ? detailEmployment.length > 0
                        : Boolean(detailEmployment)) && (
                        <div>
                          <small>
                            <BankOutlined /> Employment Type
                          </small>
                          <b>{displayValue(detailEmployment)}</b>
                        </div>
                      )}
                      {formatSalary(detailSalary) && (
                        <div>
                          <small>
                            <DollarOutlined /> Salary
                          </small>
                          <b>{formatSalary(detailSalary)}</b>
                        </div>
                      )}
                      {detailVisa && (
                        <div>
                          <small>
                            <AppstoreOutlined /> Visa Sponsorship
                          </small>
                          <b>{detailVisa}</b>
                        </div>
                      )}
                    </div>
                  )}
                  {(requiredSkills.length > 0 || bonusSkills.length > 0) && (
                    <section className="detail-skills">
                      {requiredSkills.length > 0 && (
                        <>
                          <h3>
                            <ThunderboltOutlined /> Required Skills
                          </h3>
                          <div className="skill-row">
                            {requiredSkills.map((skill) => (
                              <span key={skill}>{skill}</span>
                            ))}
                          </div>
                        </>
                      )}
                      {bonusSkills.length > 0 && (
                        <>
                          <h3>
                            <StarOutlined /> Bonus Skills
                          </h3>
                          <div className="skill-row">
                            {bonusSkills.map((skill) => (
                              <span key={skill}>{skill}</span>
                            ))}
                          </div>
                        </>
                      )}
                    </section>
                  )}
                  <section className="detail-narrative">
                    <h3>
                      <BankOutlined /> About Company
                    </h3>
                    <p>
                      {jobDetail?.shortInfo?.aboutCompany ||
                        "Company information is not available for this role."}
                    </p>
                    <h3>
                      <AppstoreOutlined /> About Role
                    </h3>
                    <p>
                      {jobDetail?.shortInfo?.aboutRole ||
                        "Role information is not available for this job."}
                    </p>
                    <h3>
                      <CheckSquareOutlined /> Responsibilities
                    </h3>
                    {responsibilities.length ? (
                      <ul>
                        {responsibilities.map((item, index) => (
                          <li key={`${index}-${item}`}>{item}</li>
                        ))}
                      </ul>
                    ) : (
                      <p>No responsibilities were provided.</p>
                    )}
                    <h3>
                      <FileProtectOutlined /> Requirements
                    </h3>
                    {requirements.length ? (
                      <ul>
                        {requirements.map((item, index) => (
                          <li key={`${index}-${item}`}>{item}</li>
                        ))}
                      </ul>
                    ) : (
                      <p>No requirements were provided.</p>
                    )}
                    <h3>
                      <GiftOutlined /> Benefits
                    </h3>
                    {benefits.length ? (
                      <ul>
                        {benefits.map((item, index) => (
                          <li key={`${index}-${item}`}>{item}</li>
                        ))}
                      </ul>
                    ) : (
                      <p>No benefits were provided.</p>
                    )}
                  </section>
                </>
              </div>
            </>
          )}
        </main>
      </div>
      {resumeWindows.length > 0 &&
        createPortal(
          <div
            className={`job-resume-windows ${activeResumeWindow ? "has-active" : ""}`}
          >
            {resumeWindows.map((item) => (
              <JobResumeWindow
                key={item.id}
                item={item}
                active={activeResumeWindow === item.id}
                user={authUser}
                profileId={profileId}
                sourceType={item.sourceType}
                resumeProfileId={item.resumeProfileId}
                onActivate={(id) =>
                  setActiveResumeWindow((current) =>
                    current === id ? null : id,
                  )
                }
                onClose={closeResumeWindow}
                onFinalized={(balance) => {
                  if (balance != null) dispatch(updateBalance(balance));
                  loadApplications();
                }}
              />
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
